import * as fancyapps from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import capitalize from "lodash/capitalize";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import { Product, ProductVideo } from "~/entities/product/types";
import { productNameFrom } from "~/entities/product/utils";
import {
  SelectedVariant,
  VariantImageAngles,
  VariantImageSizes,
} from "~/entities/variant/types";
import { useHydrated } from "~/hooks/useHydrated";
import "./fancybox.css";

type Props = {
  selectedVariant: SelectedVariant;
  product: Product;
  initialMedia: Array<Media>;
  initialMainMedia: Media | null;
};

type MediaImage = VariantImageSizes & {
  index: number;
  format: "image";
  angle: VariantImageAngles;
};

type MediaVideo = ProductVideo & {
  index: number;
  format: "video";
};

type Media = MediaImage | MediaVideo;

export const mainMediaFrom = (
  selectedVariant: SelectedVariant,
): Media | null => {
  const imageAngle: VariantImageAngles = (() => {
    if (selectedVariant.imageUrls.front) return "front";
    if (selectedVariant.imageUrls.side) return "side";
    return "back";
  })();

  if (!selectedVariant.imageUrls[imageAngle]) return null;

  return {
    index: 0,
    format: "image",
    angle: imageAngle,
    ...selectedVariant.imageUrls[imageAngle],
  };
};

export const mediaFrom = (
  selectedVariant: SelectedVariant,
  product: Product,
): Array<Media> => {
  const images: Array<Media> = [];
  let index = 0;

  for (const [angle, sizes] of Object.entries(
    selectedVariant.imageUrls,
  ) as Array<[VariantImageAngles, VariantImageSizes]>) {
    if (!sizes) continue;

    images.push({
      index,
      format: "image",
      angle,
      ...sizes,
    });

    index += 1;
  }

  const videos = product.videos.map<MediaVideo>((v, i) => ({
    ...v,
    index: index + i,
    format: "video",
  }));

  return [...images, ...videos];
};

const ProductMedia: React.FC<Props> = ({
  initialMedia,
  initialMainMedia,
  product,
  selectedVariant,
}) => {
  const isHydrated = useHydrated();
  const [media, setMedia] = useState<Array<Media>>(initialMedia);
  const [mainMedia, setMainMedia] = useState<Media | null>(initialMainMedia);

  useEffect(() => {
    if (!isHydrated) return;

    const newMainMedia = mainMediaFrom(selectedVariant);

    setMainMedia(newMainMedia);
  }, [product, selectedVariant]);

  useEffect(() => {
    if (!isHydrated) return;

    const newMedia = mediaFrom(selectedVariant, product);

    setMedia(newMedia);
  }, [product, selectedVariant]);

  const showFancybox = (startIndex: number) => {
    new fancyapps.Fancybox(
      media.map((media) => {
        if (media.format === "video") {
          return {
            src: media.url,
            thumb: media.thumbnailUrl,
          };
        } else {
          return {
            src: media.fullsize,
            thumb: media.thumbnail,
          };
        }
      }),
      {
        startIndex,
        animated: false,
        compact: false,
        wheel: false,
        Carousel: {
          fill: false,
        },
        Html: {
          videoAutoplay: false,
        },
        tpl: {
          main: `<div class="fancybox__container" role="dialog" aria-modal="true" aria-label="{{MODAL}}" tabindex="-1"><div class="fancybox__backdrop !opacity-50"></div><div class="fancybox__carousel py-10"></div>`,
        },
        Images: {
          initialSize: "fit",
        },
        closeButton: false,
        Thumbs: {
          showOnStart: false,
        },
        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: [],
          },
        },
      },
    );
  };

  const handleMainMediaClick = () => {
    if (!mainMedia) return;

    showFancybox(mainMedia.index);

    return;
  };

  const handleThumbnailClick = (index: number) => {
    if (!selectedVariant) return;
    setMainMedia(media[index]);
  };

  if (!mainMedia) return null;

  return (
    <div className="mx-auto flex h-[90vw] max-h-[450px] w-full max-w-[500px] flex-row justify-start gap-2.5 lg:h-auto lg:max-h-none lg:flex-col lg:overflow-visible">
      <div className="ml-[2vw] mr-[1vw] mt-2 flex w-[calc(75%-3vw)] items-start justify-center lg:ml-0 lg:mt-0 lg:w-full lg:bg-[10px]">
        <button
          className={`max-h-full w-full max-w-full justify-center overflow-hidden md:flex ${mainMedia.format === "image" ? "" : "max-h-[340px] items-end md:max-h-[420px]"}`}
          onClick={handleMainMediaClick}
        >
          {mainMedia.format === "image" ? (
            <img
              alt={`${productNameFrom(product, false)} ${capitalize(mainMedia.angle)} Fullsize"`}
              aria-label={`${capitalize(mainMedia.angle)} Fullsize`}
              className="max-h-full max-w-full"
              data-key={`${mainMedia.angle}-thumb`}
              data-srcset-template="{{large}} 700w, {{medium}} 1000w"
              id="main-image"
              sizes="(min-width: 500px) 700px, 70rem"
              src={mainMedia.large}
              srcSet={`${mainMedia.large} 700w, ${mainMedia.medium} 1000w`}
            />
          ) : (
            <ReactPlayer
              url={mainMedia.url}
              id="video_iframe"
              config={{
                youtube: {
                  playerVars: {
                    showinfo: 0,
                  },
                },
              }}
              aria-label={`${productNameFrom(product, false)} Video`}
              controls={true}
              width="100%"
              height="420px"
            />
          )}
        </button>
      </div>
      <div className="ml-1 mt-1.5 flex min-h-[100px] w-[25%-3vw] flex-col flex-wrap items-center justify-around lg:m-0 lg:block lg:w-full lg:gap-4">
        {media.map((media, index) => (
          <button
            key={`product-media-gallery-thumbnail-${index}`}
            onClick={() => handleThumbnailClick(index)}
            aria-current={mainMedia.index === media.index ? "true" : "false"}
            className={`flex aspect-[1/1.25] w-[15vw] max-w-[75px] cursor-pointer items-center justify-center overflow-hidden text-center outline lg:float-left lg:mx-[2%] lg:my-0 lg:max-w-[110px] lg:first-of-type:ml-0 lg:last-of-type:mr-0 ${mainMedia.index === media.index ? "outline-3 outline-blue900" : "outline-1 outline-white400"}`}
          >
            <img
              alt={`${productNameFrom(product, false)} ${media.format === "image" ? capitalize(media.angle) : "Video"} Thumbnail`}
              aria-label={`${media.format === "image" ? capitalize(media.angle) : "Video"} Thumbnail`}
              className="block max-h-full max-w-full"
              sizes="(min-width: 500px) 700px, 70rem"
              src={
                media.format === "image" ? media.thumbnail : media.thumbnailUrl
              }
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ProductMedia;
